import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ClipboardCheck, Search, Play, Loader2, UserSquare2, FileText, Inbox, Trash2 } from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { API_CONFIG } from '../../config/api.config'
import usePermissions from '../../hooks/usePermissions'

interface ExamItem {
  id: string
  title: string
  description?: string
  slug: string
  scope: 'system' | 'teacher'
  owner_teacher_id?: string
  owner_name?: string
  access_type: string
  target_class: string
  exam_type: string
  total_points?: number
  created_at?: string
  settings?: { subject?: string }
}

// Khối: 10 / 11 / 12 / HSG (pmaker bắt buộc chọn). Đề cũ lưu 'all' → 'Chưa phân khối', xếp cuối.
const CLASS_LABEL: Record<string, string> = { '10': 'Lớp 10', '11': 'Lớp 11', '12': 'Lớp 12', hsg: 'HSG', all: 'Chưa phân khối' }
const TYPE_LABEL: Record<string, string> = {
  'trac-nghiem-bai-hoc': 'Trắc nghiệm theo bài',
  'de-kiem-tra': 'Đề kiểm tra',
  'de-thi': 'Đề thi',
}
const CLASS_ORDER = ['10', '11', '12', 'hsg', 'all']

const ThiOnline: React.FC = () => {
  const navigate = useNavigate()
  const { isAdmin } = usePermissions()
  const [exams, setExams] = useState<ExamItem[]>([])
  const [loading, setLoading] = useState(true)
  const [grade, setGrade] = useState<string>('any')        // 'any' | '10' | '11' | '12' | 'all'
  const [teacher, setTeacher] = useState<string>('any')    // 'any' | tên giáo viên | 'Hệ thống'
  const [search, setSearch] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Admin xoá đề thi khỏi ngân hàng (gọi DELETE /pmaker-advanced/exam/{id})
  const handleDelete = async (e: ExamItem) => {
    if (!window.confirm(`Xoá đề "${e.title}"? Hành động này không thể hoàn tác.`)) return
    setDeletingId(e.id)
    try {
      const tk = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''
      const res = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/exam/${e.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tk}` },
      })
      if (!res.ok) throw new Error(await res.text())
      setExams((prev) => prev.filter((x) => x.id !== e.id))
    } catch (err) {
      alert('Xoá đề thất bại. Vui lòng thử lại.')
      console.error(err)
    } finally {
      setDeletingId(null)
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/published-exams`)
        const json = await res.json()
        if (json?.success && Array.isArray(json.data)) setExams(json.data)
      } catch {
        /* ignore */
      }
      setLoading(false)
    }
    load()
  }, [])

  // Hiện mọi đề CÔNG KHAI (hệ thống + giáo viên); đề giao riêng lớp/HS vẫn vào bằng link giáo viên gửi.
  // Sắp theo KHỐI LỚP và TÊN GIÁO VIÊN — học sinh tự lọc/tìm.
  const visible = useMemo(() => exams.filter((e) => e.scope === 'system' || e.access_type === 'public'), [exams])
  const teacherOf = (e: ExamItem) => (e.scope === 'system' ? 'Hệ thống' : e.owner_name || 'Giáo viên')
  const classOf = (e: ExamItem) => (CLASS_ORDER.includes(e.target_class) ? e.target_class : 'all')
  const byName = (a: string, b: string) => (a === 'Hệ thống' ? -1 : b === 'Hệ thống' ? 1 : a.localeCompare(b, 'vi'))

  const teachers = useMemo(() => {
    const count: Record<string, number> = {}
    for (const e of visible) count[teacherOf(e)] = (count[teacherOf(e)] || 0) + 1
    return Object.entries(count).sort(([a], [b]) => byName(a, b))
  }, [visible])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return visible.filter((e) => {
      if (grade !== 'any' && classOf(e) !== grade) return false
      if (teacher !== 'any' && teacherOf(e) !== teacher) return false
      if (q && !e.title.toLowerCase().includes(q) && !teacherOf(e).toLowerCase().includes(q)) return false
      return true
    })
  }, [visible, grade, teacher, search])

  // Nhóm: Khối lớp → Giáo viên
  const grouped = useMemo(() => {
    const map: Record<string, Record<string, ExamItem[]>> = {}
    for (const e of filtered) ((map[classOf(e)] ||= {})[teacherOf(e)] ||= []).push(e)
    return map
  }, [filtered])
  const gradeCount = (c: string) =>
    visible.filter((e) => (c === 'any' || classOf(e) === c) && (teacher === 'any' || teacherOf(e) === teacher)).length

  return (
    <div className="min-h-screen bg-transparent">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-bold mb-4">
            <ClipboardCheck size={16} /> Thi Online
          </div>
          <h1 className="text-4xl font-bold text-slate-800 mb-3">Ngân hàng đề thi trực tuyến</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto font-light">
            Chọn đề để làm bài và nhận điểm tự động. Đề được tạo và số hoá từ hệ thống PMaker.
          </p>
        </div>

        {/* Bộ lọc: Khối lớp · Giáo viên · Tìm kiếm */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
          <div className="flex flex-wrap gap-1.5">
            {[['any', 'Tất cả'], ['10', 'Lớp 10'], ['11', 'Lớp 11'], ['12', 'Lớp 12'], ['hsg', 'HSG'], ['all', 'Chưa phân khối']]
              .filter(([k]) => k !== 'all' || gradeCount('all') > 0).map(([k, label]) => (
              <button key={k} onClick={() => setGrade(k)}
                className={`px-3.5 py-2 rounded-xl text-sm font-bold border transition-all ${grade === k ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {label} <span className={grade === k ? 'text-blue-100' : 'text-gray-400'}>({gradeCount(k)})</span>
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 lg:ml-auto">
            <select value={teacher} onChange={(e) => setTeacher(e.target.value)}
              className="px-3 py-2.5 rounded-xl bg-white border border-gray-200 shadow-sm text-sm font-semibold text-gray-700 sm:w-56">
              <option value="any">Tất cả giáo viên</option>
              {teachers.map(([name, n]) => <option key={name} value={name}>{name} ({n})</option>)}
            </select>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 shadow-sm sm:w-64">
              <Search size={16} className="text-gray-400" />
              <input className="flex-1 bg-transparent outline-none text-sm" placeholder="Tìm tên đề hoặc giáo viên..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Danh sách: Khối lớp → Giáo viên */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-7 h-7 animate-spin text-gray-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-gray-300">
            <Inbox size={44} className="mb-3" />
            <p className="text-gray-400 font-medium">Không có đề nào phù hợp bộ lọc.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {CLASS_ORDER.filter((c) => grouped[c]).map((c) => (
              <div key={c}>
                <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-blue-500 rounded-full" /> {CLASS_LABEL[c]}
                </h2>
                <div className="space-y-6">
                  {Object.keys(grouped[c]).sort(byName).map((name) => (
                    <div key={name}>
                      <h3 className="text-sm font-bold text-slate-600 mb-2 flex items-center gap-1.5">
                        <UserSquare2 size={15} className="text-slate-400" /> {name}
                        <span className="text-slate-400 font-medium">· {grouped[c][name].length} đề</span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {grouped[c][name].map((e) => (
                          <motion.button
                            key={e.id}
                            whileHover={{ y: -4 }}
                            onClick={() => navigate(`/exam/${e.slug}`)}
                            className="text-left bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-blue-200 transition-all p-5 flex flex-col"
                          >
                            <div className="flex items-center justify-between mb-2 gap-2">
                              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                                {TYPE_LABEL[e.exam_type] || 'Đề thi'}
                              </span>
                              <span className="text-[11px] text-gray-400 flex items-center gap-1 ml-auto">
                                <FileText size={12} /> {e.total_points ?? 10} điểm
                              </span>
                            </div>
                            <h4 className="text-base font-bold text-gray-900 mb-1 line-clamp-2">{e.title}</h4>
                            {e.description && <p className="text-xs text-gray-400 line-clamp-2 flex-1">{e.description}</p>}
                            <div className="mt-2 text-xs text-gray-500">
                              {e.settings?.subject ? `${e.settings.subject} · ` : ''}{CLASS_LABEL[classOf(e)]}
                              {e.created_at ? ` · ${new Date(e.created_at).toLocaleDateString('vi-VN')}` : ''}
                            </div>
                            <div className="mt-4 flex items-center justify-between">
                              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
                                <Play size={15} /> Vào thi
                              </span>
                              {isAdmin && (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(ev) => { ev.stopPropagation(); if (deletingId !== e.id) handleDelete(e) }}
                                  onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.stopPropagation(); handleDelete(e) } }}
                                  title="Xoá đề thi (Admin)"
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-lg transition-colors"
                                >
                                  {deletingId === e.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Xoá
                                </span>
                              )}
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  )
}

export default ThiOnline
